import * as Excel from "exceljs";
import * as FileSaver from "file-saver";

/**
 * @class Xlsx - Класс для подготовки данных и генерации на их основе excel файла.
 */
class Xlsx {
  /**
   * @param { Object[] } [headers=null] - Массив объектов, где каждый объект является колонкой и хранит в себе название колонки (header) и
   * ключ (key) по которому будут браться данные из массива объектов articles
   * @param { Object[] } [articles=null] - Массив артикулов
   */
  constructor(headers = null, articles = null) {
    this.headers = headers;
    this.articles = articles;
  }

  /**
   * @param { String } company - Имя компании
   * @return { string } - Возвращает строку из имени компании и текущей даты
   */
  createFilename(company) {
    return `${company}_${new Date().toLocaleString().slice(0, 10)}.xlsx`;
  }

  /**
   * Метод возвращает массив объектов для создания простой excel таблицы без разделения
   * по категориям.
   * @return { Object[] }
   */
  createSimpleSheet(key = "key") {
    const formattedCells = this.articles.map(article =>
      this.headers.map(header => article[header[key]])
    );

    return {
      columns: this.headers,
      cells: formattedCells
    };
  }

  /**
   * Метод возвращает данные для отрисовки excel таблицы с разбивкой по категориям
   * @param { String } prop - Название свойства по которому будем делить на категории
   * @param { String[] } allowedValues - Массив разрешенных значений. Костыль для кейса при котором
   * в свойстве артикула хранится массив значений при том, что нам надо только одно из них.
   * @return { Object[] }
   */
  createSeparatedByNameSheet(prop, allowedValues = []) {
    let uniqueNames = Array.from(
      new Set(this.articles.map(item => item[prop]).flat())
    );

    if (allowedValues.length) {
      uniqueNames = uniqueNames.filter(item => allowedValues.includes(item));
    }

    const cells = uniqueNames
      .map(name => {
        const filteredArticlesByEquipment = this.articles.filter(article => {
          if (Array.isArray(article[prop])) {
            return article[prop].includes(name);
          } else {
            return article[prop] === name;
          }
        });
        const formattedArticles = filteredArticlesByEquipment.map(article =>
          this.headers.map(header => article[header.key])
        );

        return [[name], ...formattedArticles];
      })
      .flat();

    return {
      columns: this.headers,
      cells
    };
  }

  /**
   * Метод для генерации xlsx. Принимает имя компании, колонки и строки.
   * @param { String } company - Название компании
   * @param { Object[] } columns - Колонки
   * @param { Object[] } cells - Строки
   * @return {Promise<void>}
   */
  async generateXlsx(company = "", { columns, cells }) {
    const workbook = new Excel.Workbook();
    const worksheet = workbook.addWorksheet("Products");

    worksheet.columns = columns;
    worksheet.addRows(cells);

    // Все строки у которых массивы с данными по длине меньше чем
    // массив колонок мы считаем как строки с названием категории.
    // Для них выставляем стили чтобы выделить их в файле.
    const subtitleRows = worksheet.getRows(0, cells.length) || [];
    const filteredSubtitleRows = subtitleRows.filter(
      row => row.values.length <= columns.length
    );

    filteredSubtitleRows.forEach(row => {
      row.font = {
        bold: true,
        size: 14,
        color: {
          argb: "FFFFFF"
        }
      };
      row.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: {
          argb: "ea5239"
        }
      };
    });
    // Так как первая строка у нас содержит названия колонок, то
    // прописываем для них стили.
    worksheet.getRow(1).font = {
      bold: true,
      size: 14,
      color: {
        argb: "FFFFFF"
      }
    };
    worksheet.getRow(1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: {
        argb: "ea5239"
      }
    };

    const file = await workbook.xlsx.writeBuffer();
    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    const filename = this.createFilename(company);
    const blob = new Blob([file], { type: fileType });

    await FileSaver.saveAs(blob, filename);
  }
}

export default Xlsx;
